import { HttpClient } from "@/shared/lib/http-client"
import { env } from "@/config/env"

export interface GeoLocation {
  ip: string | null
  country: string | null
}

export interface IGeoLocationService {
  getLocation(): Promise<GeoLocation>
}

const EMPTY: GeoLocation = { ip: null, country: null }

/**
 * Resolves the visitor's IP and country from a Cloudflare-trace-style endpoint
 * that returns plain text `key=value` lines (`ip=...`, `loc=US`, ...).
 * Result is memoized for the page lifetime; failures degrade gracefully to nulls.
 */
export class CloudflareGeoService extends HttpClient implements IGeoLocationService {
  private cache: Promise<GeoLocation> | null = null
  private readonly endpoint: string

  constructor(endpoint: string) {
    super({ baseURL: endpoint })
    this.endpoint = endpoint
  }

  getLocation(): Promise<GeoLocation> {
    if (!this.endpoint) return Promise.resolve(EMPTY)
    if (!this.cache) {
      this.cache = this.fetchLocation().catch(() => EMPTY)
    }
    return this.cache
  }

  private async fetchLocation(): Promise<GeoLocation> {
    const text = await this.getText("")
    const parsed = this.parseTrace(text)
    return {
      ip: parsed.ip ?? null,
      country: parsed.loc ?? null,
    }
  }

  private parseTrace(text: string): Record<string, string> {
    const result: Record<string, string> = {}
    text
      .trim()
      .split("\n")
      .forEach((line) => {
        const idx = line.indexOf("=")
        if (idx > 0) {
          result[line.slice(0, idx)] = line.slice(idx + 1)
        }
      })
    return result
  }
}

export const geoLocationService: IGeoLocationService = new CloudflareGeoService(
  env.VITE_GEO_API_URL,
)
